"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Upload, File, X, CheckCircle2, AlertCircle, ShieldAlert, ChevronsUpDown, Check } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { useUserContext } from "@/app/_module/store/contexts/userContext";

import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  DialogTrigger,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/app/_module/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/app/_module/components/ui/form";
import { Input } from "@/app/_module/components/ui/input";
import { Progress } from "@/app/_module/components/ui/progress";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { Switch } from "@/app/_module/components/ui/switch";
import { Textarea } from "@/app/_module/components/ui/textarea";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/app/_module/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import { FilterPop } from "@/app/_module/components/ui/shell-popover";
import { cn } from "@/app/_module/lib/utils";

import { useResourceCategories, useAgencyCarriers } from "../hooks/use-hook";
import { resourceFormSchema } from "../schema";
import type { CreateResourceRequest } from "../types/types";
import { isDemoMode } from "@/app/_module/utils/demoMode";

type FileType = keyof typeof fileTypeConfig;
type FormData = Omit<CreateResourceRequest, "file">;

interface CreateResourceModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  description?: string;
  schema: z.ZodSchema;
  onCreate: (
    data: CreateResourceRequest,
    reset: () => void,
    close: () => void
  ) => void;
  loading?: boolean;
  uploadProgress?: number;
}

const fileTypeConfig = {
  // Documents
  PDF: { color: "bg-red-100 text-red-800 border-red-200", icon: "📄" },
  DOCX: { color: "bg-blue-100 text-blue-800 border-blue-200", icon: "📝" },
  DOC: { color: "bg-blue-100 text-blue-800 border-blue-200", icon: "📝" },
  XLSX: { color: "bg-green-100 text-green-800 border-green-200", icon: "📊" },
  XLS: { color: "bg-green-100 text-green-800 border-green-200", icon: "📊" },
  CSV: { color: "bg-green-100 text-green-800 border-green-200", icon: "📊" },
  PPTX: { color: "bg-orange-100 text-orange-800 border-orange-200", icon: "📊" },
  PPT: { color: "bg-orange-100 text-orange-800 border-orange-200", icon: "📊" },
  TXT: { color: "bg-gray-100 text-gray-800 border-gray-200", icon: "📃" },
  RTF: { color: "bg-gray-100 text-gray-800 border-gray-200", icon: "📃" },
  // OpenDocument formats
  ODT: { color: "bg-blue-100 text-blue-800 border-blue-200", icon: "📝" },
  ODS: { color: "bg-green-100 text-green-800 border-green-200", icon: "📊" },
  ODP: { color: "bg-orange-100 text-orange-800 border-orange-200", icon: "📊" },
  // Images
  JPG: { color: "bg-purple-100 text-purple-800 border-purple-200", icon: "🖼️" },
  JPEG: { color: "bg-purple-100 text-purple-800 border-purple-200", icon: "🖼️" },
  PNG: { color: "bg-purple-100 text-purple-800 border-purple-200", icon: "🖼️" },
  GIF: { color: "bg-purple-100 text-purple-800 border-purple-200", icon: "🖼️" },
  WEBP: { color: "bg-purple-100 text-purple-800 border-purple-200", icon: "🖼️" },
  SVG: { color: "bg-purple-100 text-purple-800 border-purple-200", icon: "🖼️" },
  // Video
  MP4: { color: "bg-pink-100 text-pink-800 border-pink-200", icon: "🎬" },
  WEBM: { color: "bg-pink-100 text-pink-800 border-pink-200", icon: "🎬" },
  MOV: { color: "bg-pink-100 text-pink-800 border-pink-200", icon: "🎬" },
  // Audio
  MP3: { color: "bg-indigo-100 text-indigo-800 border-indigo-200", icon: "🎵" },
  WAV: { color: "bg-indigo-100 text-indigo-800 border-indigo-200", icon: "🎵" },
  M4A: { color: "bg-indigo-100 text-indigo-800 border-indigo-200", icon: "🎵" },
  // Archives
  ZIP: { color: "bg-yellow-100 text-yellow-800 border-yellow-200", icon: "📦" },
  // eBooks
  EPUB: { color: "bg-teal-100 text-teal-800 border-teal-200", icon: "📚" },
} as const;

const getFileTypeFromExtension = (filename: string, mimeType?: string): FileType => {
  const extension = filename.split(".").pop()?.toLowerCase();
  switch (extension) {
    // Documents
    case "pdf":
      return "PDF";
    case "doc":
      return "DOC";
    case "docx":
      return "DOCX";
    case "xls":
      return "XLS";
    case "xlsx":
      return "XLSX";
    case "csv":
      return "CSV";
    case "ppt":
      return "PPT";
    case "pptx":
      return "PPTX";
    case "txt":
      return "TXT";
    case "rtf":
      return "RTF";
    // OpenDocument
    case "odt":
      return "ODT";
    case "ods":
      return "ODS";
    case "odp":
      return "ODP";
    // Images
    case "jpg":
      return "JPG";
    case "jpeg":
      return "JPEG";
    case "png":
      return "PNG";
    case "gif":
      return "GIF";
    case "webp":
      return "WEBP";
    case "svg":
      return "SVG";
    // Video
    case "mp4":
      return "MP4";
    case "webm":
      return "WEBM";
    case "mov":
      return "MOV";
    // Audio
    case "mp3":
      return "MP3";
    case "wav":
      return "WAV";
    case "m4a":
      return "M4A";
    // Archives
    case "zip":
      return "ZIP";
    // eBooks
    case "epub":
      return "EPUB";
    default:
      // Fallback to MIME type detection if extension is unknown
      if (mimeType) {
        if (mimeType === "text/csv") return "CSV";
        if (mimeType === "application/pdf") return "PDF";
        if (mimeType.includes("word")) return "DOCX";
        if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return "XLSX";
        if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "PPTX";
        if (mimeType.startsWith("image/jpeg")) return "JPG";
        if (mimeType.startsWith("image/png")) return "PNG";
        if (mimeType.startsWith("image/gif")) return "GIF";
        if (mimeType.startsWith("image/webp")) return "WEBP";
        if (mimeType.startsWith("image/svg")) return "SVG";
        if (mimeType.startsWith("video/mp4")) return "MP4";
        if (mimeType.startsWith("video/webm")) return "WEBM";
        if (mimeType.startsWith("video/quicktime")) return "MOV";
        if (mimeType.startsWith("audio/mpeg")) return "MP3";
        if (mimeType.startsWith("audio/wav")) return "WAV";
        if (mimeType.startsWith("audio/mp4") || mimeType.startsWith("audio/x-m4a")) return "M4A";
        if (mimeType.includes("zip")) return "ZIP";
        if (mimeType.includes("epub")) return "EPUB";
        if (mimeType === "text/plain") return "TXT";
      }
      return "PDF";
  }
};

// Validate file size (max 10MB for all files)
const validateFileSize = (file: File): boolean => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  return file.size <= maxSize;
};

// Format file size for display
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(2) + " " + sizes[i];
};

const CreateResourceModal = function CreateResourceModal({
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  title = "Create Resource",
  description,
  onCreate,
  loading = false,
  uploadProgress = 0,
}: CreateResourceModalProps) {
  // Internal state for self-contained modal
  const [internalOpen, setInternalOpen] = useState(false);

  // Use external props if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const onOpenChange = externalOnOpenChange || setInternalOpen;

  // Get user context
  const { user } = useUserContext();

  // Get agencyId from user context (nested in user.user or currentAgency)
  const userAgencyId = user?.user?.agencyId || user?.currentAgency?.agency?.id;

  const { data: categories = [], isLoading: categoriesLoading, error: categoriesError } =
    useResourceCategories();

  // Fetch agency carriers
  const { data: agencyCarriers = [], isLoading: carriersLoading } = useAgencyCarriers();

  // Debug logging for carriers
  React.useEffect(() => {
  }, [agencyCarriers, carriersLoading]);

  // Fallback categories if API fails
  const fallbackCategories = React.useMemo(() => [
    { id: 'forms', name: 'Forms', description: 'Application forms and documents', iconUrl: '', createdAt: '', updatedAt: '' },
    { id: 'contracts', name: 'Contracts', description: 'Contract templates and agreements', iconUrl: '', createdAt: '', updatedAt: '' },
    { id: 'presentations', name: 'Presentations', description: 'Sales presentations and materials', iconUrl: '', createdAt: '', updatedAt: '' },
    { id: 'training', name: 'Training', description: 'Training materials and resources', iconUrl: '', createdAt: '', updatedAt: '' },
    { id: 'compliance', name: 'Compliance', description: 'Compliance documents and guidelines', iconUrl: '', createdAt: '', updatedAt: '' }
  ], []);

  // Use fallback categories if API fails or returns empty
  const availableCategories = React.useMemo(() => {
    if (categoriesError || (!categoriesLoading && categories.length === 0)) {
      return fallbackCategories;
    }
    return categories;
  }, [categories, categoriesError, categoriesLoading, fallbackCategories]);

  // Debug logging for categories
  React.useEffect(() => {
  }, [categories, categoriesLoading, categoriesError, availableCategories]);

  // Helper function to get category display value (UUID or friendly name)
  const getCategoryValue = (category: { id: string; name: string }) => {
    // Backend now accepts both UUIDs and friendly names
    // Always use ID for backend lookup (consistent behavior)
    return category.id;
  };

  // Debug logging
  React.useEffect(() => {
  }, [categories, availableCategories, categoriesLoading, categoriesError]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [carrierOpen, setCarrierOpen] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(resourceFormSchema),
    mode: "onChange",
    defaultValues: {
      title: "",
      description: "",
      categoryId: "",
      carrier: "",
      fileType: "",
      tags: [],
      scope: "personal",
      isSensitive: false,
      sensitiveDataTypes: [],
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isValid },
    reset,
    setValue,
    watch,
  } = form;

  useEffect(() => {
    if (!open) {
      reset({
        title: "",
        description: "",
        categoryId: "",
        carrier: "",
        fileType: "",
        tags: [],
        scope: "personal",
        isSensitive: false,
        sensitiveDataTypes: [],
      });
      setSelectedFile(null);
      setFileError(null);
    }
  }, [open, reset]);

  const processFile = (file: File) => {
    setFileError(null);

    // Validate file size
    if (!validateFileSize(file)) {
      setFileError("File size must be less than 10MB");
      return;
    }

    // Validate file type
    const allowedTypes = [
      // Documents
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "text/plain",
      "text/csv",
      "application/rtf",
      // OpenDocument
      "application/vnd.oasis.opendocument.text",
      "application/vnd.oasis.opendocument.spreadsheet",
      "application/vnd.oasis.opendocument.presentation",
      // Images
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/svg+xml",
      // Video
      "video/mp4",
      "video/webm",
      "video/quicktime",
      // Audio
      "audio/mpeg",
      "audio/wav",
      "audio/mp4",
      "audio/x-m4a",
      // Archives
      "application/zip",
      "application/x-zip-compressed",
      // eBooks
      "application/epub+zip",
    ];

    if (
      !allowedTypes.includes(file.type) &&
      !file.name.match(/\.(pdf|doc|docx|xls|xlsx|csv|ppt|pptx|txt|rtf|odt|ods|odp|jpg|jpeg|png|gif|webp|svg|mp4|webm|mov|mp3|wav|m4a|zip|epub)$/i)
    ) {
      setFileError(
        "Please select a valid file (documents, images, video, audio, or archives)"
      );
      return;
    }

    setSelectedFile(file);

    // Auto-detect and set file type (pass MIME type for fallback detection)
    const detectedFileType = getFileTypeFromExtension(file.name, file.type);
    setValue("fileType", detectedFileType);

    // Add file type as a tag automatically
    const currentTags = watch("tags") || [];
    const filteredCurrentTags = currentTags.filter(
      (tag) => !Object.keys(fileTypeConfig).includes(tag)
    );
    const newTags = [detectedFileType, ...filteredCurrentTags];
    setValue("tags", newTags);

    // Auto-fill title if empty
    if (!watch("title")) {
      const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
      setValue("title", nameWithoutExtension);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    } else {
      setSelectedFile(null);
      setFileError(null);
      setValue("fileType", "");
      // Remove only file type tags, keep user tags
      const currentTags = watch("tags") || [];
      const filteredTags = currentTags.filter(
        (tag) => !Object.keys(fileTypeConfig).includes(tag)
      );
      setValue("tags", filteredTags);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFileError(null);
    setValue("fileType", "");
    // Remove only file type tags, keep user tags
    const currentTags = watch("tags") || [];
    const filteredTags = currentTags.filter(
      (tag) => !Object.keys(fileTypeConfig).includes(tag)
    );
    setValue("tags", filteredTags);
  };

  const onSubmit = (data: FormData) => {
    // DEMO SANDBOX: the form is fully explorable, the upload is not.
    //
    // Creating a resource writes a file into Supabase storage and a row that the seat
    // reset does not remove — so one visitor's upload becomes permanent furniture for
    // everyone after them, and it burns real storage and egress. Blocked at submit rather
    // than at the dropzone so a visitor can still fill the form in, pick a sample file and
    // see exactly what the flow looks like before being asked for the trial.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Uploading resources is disabled in the demo sandbox. Start a free trial to build your own resource library.',
        }),
      );
      return;
    }

    if (!selectedFile) {
      setFileError("Please select a file to upload");
      return;
    }

    // Ensure tags is always an array and filter out empty tags
    const tags = Array.isArray(data.tags)
      ? data.tags.filter((tag) => tag && typeof tag === "string" && tag.trim())
      : [];

    const completeData: CreateResourceRequest = {
      ...data,
      tags,
      file: selectedFile,
    };

    onCreate(completeData, reset, () => setInternalOpen(false));
  };

  const canSubmit = isValid && selectedFile !== null && !fileError;

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Upload className="h-4 w-4" />
          {title}
        </Button>
      </DialogTrigger>
      <ResponsiveDialogContent size="2xl" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description || "Upload and organize your resource files"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* File Upload Section */}
            <div className="space-y-4">
              <label className="text-sm font-medium" htmlFor="file-input">
                Upload Document
              </label>

              {isDemoMode() && !selectedFile ? (
                /*
                  Sample files instead of a browse dialog.
                  Same reasoning as the demo CSVs in the lead importer: a visitor has no
                  file of ours to pick, so a dropzone that opens their own filesystem is a
                  dead end. Each button fetches a real asset from /demo and wraps it in a
                  File, so everything downstream — preview, name, size, type — behaves
                  exactly as a genuine selection would. Nothing is uploaded; submit offers
                  the trial.
                */
                <div className="rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 p-6 text-center">
                  <p className="text-sm font-medium text-slate-900">
                    Pick a sample file to preview the flow
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Uploading your own files needs a trial account.
                  </p>
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                    {[
                      { label: "Final Expense Brochure.pdf", src: "/demo/sample-policy.pdf", type: "application/pdf" },
                      { label: "Carrier Rate Sheet.pdf", src: "/demo/sample-policy.pdf", type: "application/pdf" },
                      { label: "Objection Handling Guide.pdf", src: "/demo/sample-policy.pdf", type: "application/pdf" },
                    ].map((sample) => (
                      <button
                        key={sample.label}
                        type="button"
                        onClick={async () => {
                          try {
                            const res = await fetch(sample.src);
                            const blob = await res.blob();
                            // window.File, not File — the lucide `File` ICON is imported
                            // into this module and shadows the DOM constructor.
                            setSelectedFile(
                              new window.File([blob], sample.label, { type: sample.type }),
                            );
                            setFileError("");
                          } catch {
                            setFileError("Could not load the sample file.");
                          }
                        }}
                        className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-medium text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-50"
                      >
                        {sample.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : !selectedFile ? (
                <div
                  onClick={() => document.getElementById("file-input")?.click()}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  role="button"
                  tabIndex={0}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    dragActive
                      ? "border-primary bg-primary/5"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      document.getElementById("file-input")?.click();
                    }
                  }}
                >
                  <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-medium">
                    Drop your file here or click to browse
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Supports documents, images, video, audio, and archives (max 10MB)
                  </p>
                  <Input
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.rtf,.odt,.ods,.odp,.jpg,.jpeg,.png,.gif,.webp,.svg,.mp4,.webm,.mov,.mp3,.wav,.m4a,.zip,.epub"
                    className="hidden"
                    id="file-input"
                    onChange={handleFileChange}
                    type="file"
                  />
                </div>
              ) : (
                <div className="border rounded-lg p-4 bg-green-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <File className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium">{selectedFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(selectedFile.size)} •{" "}
                          {watch("fileType") || getFileTypeFromExtension(selectedFile.name, selectedFile.type)}
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-700">
                            Ready to upload
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      className="text-gray-500 hover:text-red-500"
                      onClick={removeFile}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {!!fileError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{fileError}</AlertDescription>
                </Alert>
              )}

              {!!loading && uploadProgress > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} />
                </div>
              )}
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Title */}
              <FormField
                control={control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter resource title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* File Type */}
              <FormField
                control={control}
                name="fileType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>File Type</FormLabel>
                    <FormControl>
                      <Select
                        disabled
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Auto-detected" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(fileTypeConfig).map((type) => (
                            <SelectItem key={type} value={type}>
                              <div className="flex items-center gap-2">
                                <span>
                                  {fileTypeConfig[type as FileType].icon}
                                </span>
                                {type}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Description */}
            <FormField
              control={control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      className="resize-none"
                      placeholder="Describe the resource and its purpose..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Resource Visibility - Show agency options only for users in an agency */}
            <FormField
              control={control}
              name="scope"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Visibility</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || 'personal'}
                      disabled={!userAgencyId} // Disable for independent agents
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Who can see this resource?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personal">
                          <span className="font-medium">Personal</span>
                          <span className="text-muted-foreground ml-2">- Only visible to you</span>
                        </SelectItem>
                        {/* Agency options based on role */}
                        {userAgencyId && (
                          <>
                            <SelectItem value="agency-only">
                              <span className="font-medium">My Agency</span>
                              <span className="text-muted-foreground ml-2">- Direct team only</span>
                            </SelectItem>
                            {/* Only owners/admins can share to sub-agencies or agency-wide */}
                            {['owner', 'admin'].includes(((user as any)?.currentAgency?.role || '').toLowerCase()) && (
                              <SelectItem value="agency-and-below">
                                <span className="font-medium">My Agency + Subs</span>
                                <span className="text-muted-foreground ml-2">- Team & sub-agencies</span>
                              </SelectItem>
                            )}
                            {['owner', 'admin'].includes(((user as any)?.currentAgency?.role || '').toLowerCase()) && !(user as any)?.currentAgency?.agency?.parentAgencyId && (
                              <SelectItem value="agency-wide">
                                <span className="font-medium">Agency Wide</span>
                                <span className="text-muted-foreground ml-2">- Entire organization</span>
                              </SelectItem>
                            )}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {!userAgencyId && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Join an agency to share resources with your team
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Sensitive Info Toggle */}
            <FormField
              control={control}
              name="isSensitive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 bg-amber-50/50 border-amber-200">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base flex items-center gap-2">
                      <ShieldAlert className="h-4 w-4 text-amber-600" />
                      Contains Sensitive Information
                    </FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Mark this resource if it contains SSN, DOB, financial data, or other PII.
                      {watch("fileType")?.toLowerCase() === "csv" && (
                        <span className="block text-amber-600 mt-1">
                          CSV files are automatically scanned for sensitive data.
                        </span>
                      )}
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category */}
              <FormField
                control={control}
                name="categoryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category *</FormLabel>
                    <FormControl>
                      <Select
                        disabled={categoriesLoading}
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              categoriesLoading
                                ? "Loading categories..."
                                : categoriesError
                                ? "Using fallback categories"
                                : availableCategories.length === 0
                                ? "No categories available"
                                : "Select category"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {categoriesError && (
                            <SelectItem value="__error__" disabled>
                              ⚠️ API Error - Using fallback categories
                            </SelectItem>
                          )}
                          {!categoriesError && availableCategories.length === 0 && !categoriesLoading && (
                            <SelectItem value="__empty__" disabled>
                              No categories found
                            </SelectItem>
                          )}
                          {availableCategories.map((category) => (
                            <SelectItem key={category.id} value={getCategoryValue(category)}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {categoriesError && (
                      <div className="text-sm text-yellow-600 mt-1 flex items-center justify-between">
                        <span>Using fallback categories due to API error.</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onOpenChange(false)}
                          className="ml-2 h-6 px-2 text-xs"
                        >
                          Close
                        </Button>
                      </div>
                    )}
                  </FormItem>
                )}
              />

              {/* Carrier */}
              <FormField
                control={control}
                name="carrier"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Insurance Carrier *</FormLabel>
                    <FilterPop
                      open={carrierOpen}
                      onOpenChange={setCarrierOpen}
                      modal
                      className="w-[--radix-popover-trigger-width] p-0"
                      trigger={
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={carrierOpen}
                            className="w-full justify-between font-normal"
                          >
                            {field.value || (carriersLoading ? "Loading carriers..." : "Select carrier")}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      }
                    >
                        <Command>
                          <CommandInput placeholder="Search carriers..." />
                          <CommandList className="max-h-[200px]">
                            <CommandEmpty>No carriers found.</CommandEmpty>
                            <CommandGroup>
                              {agencyCarriers.map((carrier) => (
                                <CommandItem
                                  key={carrier.name}
                                  value={carrier.name}
                                  onSelect={() => {
                                    field.onChange(carrier.name);
                                    setCarrierOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      field.value === carrier.name ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {carrier.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                    </FilterPop>
                    <FormMessage />
                    {agencyCarriers.length === 0 && !carriersLoading && (
                      <p className="text-sm text-yellow-600 mt-1">
                        Please configure carriers in Settings before creating resources.
                      </p>
                    )}
                  </FormItem>
                )}
              />
            </div>

            {/* Tags */}
            <FormField
              control={control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tags (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Add tags separated by commas: featured, popular, training"
                      onChange={(e) => {
                        const inputValue = e.target.value.trim();

                        if (!inputValue) {
                          const fileType = watch("fileType");
                          const newTags = fileType ? [fileType] : [];
                          field.onChange(newTags);
                          return;
                        }

                        const inputTags = inputValue
                          .split(",")
                          .map((tag) => tag.trim())
                          .filter(Boolean); // Remove empty strings

                        // Keep the file type tag and add user tags
                        const fileType = watch("fileType");
                        const allTags = fileType
                          ? [fileType, ...inputTags]
                          : inputTags;

                        field.onChange(allTags);
                      }}
                      value={(field.value || [])
                        .filter(
                          (tag) => !Object.keys(fileTypeConfig).includes(tag)
                        )
                        .join(", ")}
                    />
                  </FormControl>
                  <FormMessage />
                  {!!field.value &&
                    Array.isArray(field.value) &&
                    field.value.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {field.value.map((tag, index) => {
                          const isFileType =
                            Object.keys(fileTypeConfig).includes(tag);
                          const config = isFileType
                            ? fileTypeConfig[tag as FileType]
                            : null;

                          return (
                            <Badge
                              key={index}
                              className={config ? config.color : ""}
                              variant="secondary"
                            >
                              {!!config && (
                                <span className="mr-1">{config.icon}</span>
                              )}
                              {tag}
                              {!!isFileType && (
                                <span className="ml-1 text-xs">(auto)</span>
                              )}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                </FormItem>
              )}
            />

            <DialogFooter className="flex justify-between">
              <DialogClose asChild>
                <Button disabled={loading} type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button disabled={!canSubmit || loading} type="submit">
                {loading ? (
                  <React.Fragment>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Uploading...
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Resource
                  </React.Fragment>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </ResponsiveDialogContent>
    </Dialog>
  );
};

export default CreateResourceModal;